import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall } from '../../utils/fetchAPI';
import { useOptionalAuth } from '../../context/AuthContext';
import { siretProblem } from '../../utils/siret';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';

const EMPTY_LISTING = {
  categoryUuid: '',
  name: '',
  description: '',
  neighborhoodUuid: '',
  phone: '',
  contactEmail: '',
  website: '',
  street: '',
  postalCode: '',
  legalName: '',
  siret: '',
  legalStatus: 'COMPANY',
  kind: 'SERVICE',
  openingHours: [],
  citySlug: '',
};

/**
 * Empty strings must be sent as null: the API validates an email or a URL as
 * soon as the field is present, and '' is not a valid one.
 */
export function nullIfBlank(value) {
  const trimmed = String(value ?? '').trim();

  return '' === trimmed ? null : trimmed;
}

export function toCategoryChoices(tree) {
  return tree.flatMap((root) => [
    { uuid: root.uuid, name: root.name, depth: 0, kind: root.kind || 'SERVICE' },
    ...(root.children || []).map((child) => ({
      uuid: child.uuid,
      name: child.name,
      depth: 1,
      kind: child.kind || root.kind || 'SERVICE',
    })),
  ]);
}

/**
 * Whether the account still has business details to fill in.
 *
 * `ready_to_submit` is computed by the model and returned on the listing, so
 * this never reimplements the rule — the two could not disagree even if the
 * rule changed. No listing at all counts as incomplete.
 */
export function needsOnboarding(listing) {
  return null === listing || undefined === listing || false === listing.ready_to_submit;
}

/**
 * The signed-in professional's own listing: read it, save it, submit it.
 *
 * Where the business is starts as where the owner lives, and can be changed:
 * a baker can live in one town and keep a shop in the next. A neighbourhood
 * belongs to a city, so the list of neighbourhoods follows the city chosen.
 */
export function useMyListing() {
  const { user } = useOptionalAuth();
  const [listing, setListing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_LISTING);
  const [allCategories, setAllCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [notice, setNotice] = useState('');

  const applyListing = useCallback((data) => {
    setListing(data);

    if (!data) {
      return;
    }

    setFormData({
      categoryUuid: data.category.uuid,
      name: data.name || '',
      description: data.description || '',
      neighborhoodUuid: data.neighborhood?.uuid || '',
      phone: data.contact.phone || '',
      contactEmail: data.contact.email || '',
      website: data.contact.website || '',
      street: data.address.street || '',
      postalCode: data.address.postal_code || '',
      legalName: data.legal?.name || '',
      siret: data.legal?.siret || '',
      legalStatus: data.legal?.status || 'COMPANY',
      kind: data.kind || 'SERVICE',
      openingHours: data.opening_hours || [],
      citySlug: data.city?.slug || '',
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);

        const [listingResponse, categoriesResponse] = await Promise.all([
          apiCall('/me/listing', { method: 'GET' }),
          apiCall('/categories', { method: 'GET' }),
        ]);

        if (cancelled) return;

        if (!listingResponse.ok) {
          throw new Error('Impossible de charger votre fiche');
        }

        const listingData = await listingResponse.json();
        const categoriesData = await categoriesResponse.json().catch(() => ({}));

        applyListing(listingData.data);
        setAllCategories(toCategoryChoices(categoriesData.data || []));

        const citiesResponse = await apiCall('/cities', { method: 'GET' });
        const citiesData = await citiesResponse.json().catch(() => ({}));

        if (!cancelled) {
          setCities(citiesData.data || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [applyListing]);

  // A listing not written yet goes where its owner lives, or in the only city
  // there is. A saved one says where it is, and applyListing puts that here.
  const defaultCitySlug = user?.city?.slug || cities[0]?.slug || '';

  useEffect(() => {
    if (!formData.citySlug && defaultCitySlug) {
      setFormData((prev) => (prev.citySlug ? prev : { ...prev, citySlug: defaultCitySlug }));
    }
  }, [formData.citySlug, defaultCitySlug]);

  useEffect(() => {
    let cancelled = false;

    if (!formData.citySlug) {
      setNeighborhoods([]);
      return undefined;
    }

    const loadNeighborhoods = async () => {
      try {
        const response = await apiCall(`/cities/${formData.citySlug}/neighborhoods`, { method: 'GET' });
        const data = await response.json().catch(() => ({}));

        if (!cancelled) {
          setNeighborhoods(response.ok ? data.data || [] : []);
        }
      } catch {
        if (!cancelled) {
          setNeighborhoods([]);
        }
      }
    };

    loadNeighborhoods();

    return () => {
      cancelled = true;
    };
  }, [formData.citySlug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      // The two trees have nothing in common, so a category chosen as a
      // service cannot survive becoming a shop — and the API refuses the
      // pairing outright. Clear it rather than let them press save to find
      // out.
      if ('kind' === name && value !== prev.kind) {
        return {
          ...prev,
          kind: value,
          categoryUuid: '',
          // A shopfront is always a business: the model refuses the pairing,
          // so the form does not offer it.
          legalStatus: 'COMMERCE' === value ? 'COMPANY' : prev.legalStatus,
        };
      }

      // A neighbourhood of the city just left is not one of the new city's,
      // and the API refuses the pair.
      if ('citySlug' === name && value !== prev.citySlug) {
        return { ...prev, citySlug: value, neighborhoodUuid: '' };
      }

      return { ...prev, [name]: value };
    });
  };

  // The hours are a list, not a field of the form, so the editor hands over
  // the whole new list instead of an input event.
  const setOpeningHours = (openingHours) => setFormData((prev) => ({ ...prev, openingHours }));

  const save = async (e) => {
    e?.preventDefault();
    setError('');
    setNotice('');
    setFieldErrors({});

    // Checked before the request rather than after: the API answers a bad
    // check digit with a 400 whose message is not attached to a field, so a
    // round trip would put it in the page-level alert instead of under the
    // input the professional is looking at.
    const siretError = 'COMPANY' === formData.legalStatus ? siretProblem(formData.siret) : null;

    if (siretError) {
      setFieldErrors({ siret: siretError });
      setError('Corrigez les champs signalés ci-dessous');
      return false;
    }

    setSaving(true);

    const payload = {
      categoryUuid: formData.categoryUuid,
      name: formData.name,
      description: nullIfBlank(formData.description),
      neighborhoodUuid: nullIfBlank(formData.neighborhoodUuid),
      phone: nullIfBlank(formData.phone),
      contactEmail: nullIfBlank(formData.contactEmail),
      website: nullIfBlank(formData.website),
      street: nullIfBlank(formData.street),
      postalCode: nullIfBlank(formData.postalCode),
      // A private individual has neither, and sending what was typed before
      // the status changed would keep asking the model for a SIRET.
      legalName: 'COMPANY' === formData.legalStatus ? nullIfBlank(formData.legalName) : null,
      siret: 'COMPANY' === formData.legalStatus ? nullIfBlank(formData.siret) : null,
      legalStatus: formData.legalStatus,
      kind: formData.kind,
      // A shopfront has hours; somebody who comes to you has none to show.
      openingHours: 'COMMERCE' === formData.kind && formData.openingHours.length > 0 ? formData.openingHours : null,
      citySlug: nullIfBlank(formData.citySlug),
    };

    try {
      const response = await apiCall('/me/listing', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // The API validates in French where it can and in English where the
        // rule is generic; the professional reads one language.
        if (422 === response.status && data.violations) {
          setFieldErrors(translateViolations(data.violations));
          setError('Corrigez les champs signalés ci-dessous');
        } else {
          setError(translateApiMessage(data.error) || 'Enregistrement impossible');
        }
        return false;
      }

      applyListing(data.data);
      setNotice(
        'PENDING' === data.data?.status
          ? 'Enregistré. Votre fiche repasse par une validation avant publication.'
          : 'Enregistré.'
      );
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    setError('');
    setNotice('');
    setSaving(true);

    try {
      const response = await apiCall('/me/listing/submit', { method: 'POST' });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        // The model words its refusals in English, quoting the listing's name.
        setError(translateApiMessage(data.error) || 'Envoi impossible');
        return false;
      }

      applyListing(data.data);
      setNotice('Votre fiche est envoyée. Vous serez prévenu par email de la décision.');
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Each kind browses its own tree: "Boulangerie" is not somewhere a plumber
  // files themselves, and the API refuses a listing filed in the other one.
  const categories = useMemo(
    () => allCategories.filter((category) => category.kind === formData.kind),
    [allCategories, formData.kind]
  );

  return {
    listing,
    formData,
    categories,
    cities,
    // Asked only when there is a choice, like at sign-up: a question with one
    // possible answer is friction.
    choosesCity: cities.length > 1,
    neighborhoods,
    loading,
    saving,
    error,
    fieldErrors,
    notice,
    handleInputChange,
    setOpeningHours,
    save,
    submit,
    canSubmit: Boolean(formData.name && formData.categoryUuid)
      // A private individual has no legal identity to give: the model says so
      // too, through missing_to_submit.
      && ('COMPANY' !== formData.legalStatus
        || (Boolean(nullIfBlank(formData.legalName) && nullIfBlank(formData.siret))
          && null === siretProblem(formData.siret))),
    // What the API says is still missing, so the wizard and the banner speak
    // with the model's voice rather than guessing.
    missingToSubmit: listing?.missing_to_submit ?? ['legal_name', 'siret'],
    needsOnboarding: needsOnboarding(listing),
    applyListing,
  };
}
